import React, { useState } from 'react';
import { 
  MessageSquare, 
  FileText, 
  Image, 
  FileSpreadsheet, 
  Volume2, 
  Upload, 
  User, 
  Globe,
  BookOpen,
  ChevronRight,
  ChevronDown,
  Cpu,
  HelpCircle,
  Edit3,
  Camera
} from 'lucide-react';
import SampleWorkflows from './SampleWorkflows';

const Sidebar = ({ onLoadSampleWorkflow }) => {
  const [activeTab, setActiveTab] = useState('blocks');
  
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const nodeBlocks = [
    {
      category: 'Content Generation',
      blocks: [
        {
          type: 'prompt',
          label: 'Prompt Block',
          description: 'Create a text prompt with variables',
          icon: MessageSquare,
          color: 'blue'
        },
        {
          type: 'textGen',
          label: 'Gemini Text Gen',
          description: 'Generate educational content using Gemini',
          icon: FileText,
          color: 'purple'
        },
        {
          type: 'visual',
          label: 'Visual Generator',
          description: 'Create diagrams and visual aids',
          icon: Image,
          color: 'green'
        }
      ]
    },
    {
      category: 'Question Generation',
      blocks: [
        {
          type: 'mcq',
          label: 'MCQ Generator',
          description: 'Create multiple choice questions efficiently',
          icon: HelpCircle,
          color: 'blue'
        },
        {
          type: 'shortAnswer',
          label: 'Short Answer',
          description: 'Generate short answer & fill-in-the-blank questions',
          icon: Edit3,
          color: 'green'
        },
        {
          type: 'worksheet',
          label: 'Full Worksheet',
          description: 'Generate comprehensive multi-grade worksheets',
          icon: FileSpreadsheet,
          color: 'yellow'
        }
      ]
    },
    {
      category: 'Media & Analysis',
      blocks: [
        {
          type: 'audio',
          label: 'Text to Speech',
          description: 'Convert text to 30-second audio clips',
          icon: Volume2,
          color: 'pink'
        },
        {
          type: 'imageAnalysis',
          label: 'Image Analysis',
          description: 'Analyze educational images with Gemini Vision',
          icon: Camera,
          color: 'purple'
        },
        {
          type: 'upload',
          label: 'Upload Image',
          description: 'Upload and analyze textbook images',
          icon: Upload,
          color: 'orange'
        }
      ]
    },
    {
      category: 'Input & Integration',
      blocks: [
        {
          type: 'input',
          label: 'Student Input',
          description: 'Collect student responses or questions',
          icon: User,
          color: 'indigo'
        },
        {
          type: 'api',
          label: 'External API',
          description: 'Connect to Wikipedia, NCERT, or other APIs',
          icon: Globe,
          color: 'gray'
        },
        {
          type: 'offlineLLM',
          label: 'Backend Agent',
          description: 'Connect to your backend agent API',
          icon: Cpu,
          color: 'purple'
        }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      purple: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
      green: 'bg-green-50 border-green-200 hover:bg-green-100',
      yellow: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
      pink: 'bg-pink-50 border-pink-200 hover:bg-pink-100',
      orange: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
      indigo: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
      gray: 'bg-gray-50 border-gray-200 hover:bg-gray-100'
    };
    return colorMap[color] || colorMap.gray;
  };

  const getIconColor = (color) => {
    const colorMap = {
      blue: 'text-blue-600',
      purple: 'text-purple-600',
      green: 'text-green-600',
      yellow: 'text-yellow-600',
      pink: 'text-pink-600',
      orange: 'text-orange-600',
      indigo: 'text-indigo-600',
      gray: 'text-gray-600'
    };
    return colorMap[color] || colorMap.gray;
  };

  return (
    <div className="sidebar">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('blocks')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'blocks'
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Blocks
          </button>
          <button
            onClick={() => setActiveTab('samples')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'samples'
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Templates
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'blocks' ? (
        <>
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Workflow Blocks</h2>
            <p className="text-sm text-gray-600">Drag blocks to the canvas to build your flow</p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2">
            {nodeBlocks.map((category) => (
              <div key={category.category} className="mb-6">
                <div className="px-3 py-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {category.category}
                  </h3>
                </div>
                
                <div className="space-y-2">
                  {category.blocks.map((block) => {
                    const IconComponent = block.icon;
                    return (
                      <div
                        key={block.type}
                        className={`sidebar-item ${getColorClasses(block.color)}`}
                        onDragStart={(event) => onDragStart(event, block.type)}
                        draggable
                      >
                        <div className="flex items-start space-x-3">
                          <IconComponent 
                            size={20} 
                            className={`mt-0.5 ${getIconColor(block.color)}`} 
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {block.label}
                            </div>
                            <div className="text-xs text-gray-500 mt-1 leading-tight">
                              {block.description}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500 space-y-1">
              <div className="flex justify-between">
                <span>Categories:</span>
                <span>{nodeBlocks.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Blocks:</span>
                <span>{nodeBlocks.reduce((sum, cat) => sum + cat.blocks.length, 0)}</span>
              </div>
              <div className="text-center pt-2">
                <span className="font-medium">Sahayak Flow v2.0</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <SampleWorkflows onLoadWorkflow={onLoadSampleWorkflow} />
        </div>
      )}
    </div>
  );
};

export default Sidebar;